import React from 'react';
import { renderToString } from 'react-dom/server';
import _ from 'lodash';
import moment from 'moment';
import StackedAreaChart from 'components/StackedAreaChart';
import { getColor } from 'common/constants/_colors';
import CycleTimeTooltip from 'components/Tooltips/CycleTimeTooltip';
import TooltipTitle from 'components/Tooltips/TooltipTitle';

export const resolveTickConfig = yData => {
  const maxTotalCycleTime = _.maxBy(yData, 'totalCycleTime');
  if (!maxTotalCycleTime || !maxTotalCycleTime.totalCycleTime) {
    return null;
  }
  const cycleTimeDuration = moment.duration(
    maxTotalCycleTime.totalCycleTime,
    'minutes'
  );
  if (cycleTimeDuration.asDays() > 4) {
    const daysInInterval = Math.ceil(cycleTimeDuration.asDays() / 7);
    return {
      interval: 60 * 24 * daysInInterval,
      format: value => value / 60 / 24 + ' days'
    };
  } else {
    let hrsInInterval = Math.ceil(cycleTimeDuration.asHours() / 7);
    if (hrsInInterval <= 4) {
      hrsInInterval = 4;
    } else {
      hrsInInterval =
        hrsInInterval <= 8 ? 8 : hrsInInterval + 8 - (hrsInInterval % 8);
    }
    return {
      interval: 60 * hrsInInterval,
      format: value => value / 60 + 'h'
    };
  }
};

const CycleTimeTrendWidget = ({ data = {}, metricsCalculationConfig = {} }) => {
  const dataX = data.x || {};
  const xCategories = Object.keys(dataX);
  const yData = _.isEmpty(data.y) ? [] : data.y;
  const tickConfig = resolveTickConfig(yData) || {
    interval: 60,
    format: value => value / 60 + 'h'
  };
  return (
    <StackedAreaChart
      reverseLegends
      chartOptionsProp={{
        xAxis: {
          categories: xCategories,
          labels: {
            formatter() {
              const iteration = dataX[this.value];
              return _.get(iteration, 'name', '');
            }
          },
          reversed: true,
          crosshair: {
            width: 1,
            dashStyle: 'Solid'
          }
        },
        yAxis: {
          title: {
            text: ''
          },
          min: 0,
          tickInterval: tickConfig.interval,
          labels: {
            formatter() {
              return tickConfig.format(this.value);
            }
          }
        },
        tooltip: {
          shared: true,
          formatter() {
            const iterationId = this.x;
            const dataX = data.x[iterationId];
            const dataY = yData[_.get(dataX, 'index')] || {};
            const iterationName = _.get(dataX, 'name', '');

            return renderToString(
              <CycleTimeTooltip
                scale={0.75}
                metricsCalculationConfig={metricsCalculationConfig}
                isAvg
                totalTime={dataY.avgTime}
                title={
                  <TooltipTitle boldText={iterationName} text={dataX.dates} />
                }
                configurations={dataY.cycleTimeConfigurations}
              />
            );
          }
        },
        series: [
          {
            name: 'DEPLOY',
            data: yData.flatMap(({ timeToProd }) => timeToProd || 0),
            color: getColor('pastelGreen'),
            lineWidth: 0
          },
          {
            name: 'REVIEW',
            data: yData.flatMap(({ timeToMerged }) => timeToMerged || 0),
            color: getColor('melrose'),
            lineWidth: 0
          },
          {
            name: 'PICKUP',
            data: yData.flatMap(({ timeToReview }) => timeToReview || 0),
            color: getColor('indigo'),
            lineWidth: 0
          },
          {
            name: 'CODING',
            data: yData.flatMap(({ timeToPr }) => timeToPr || 0),
            color: getColor('dodgerBlue'),
            lineWidth: 0
          }
        ]
      }}
    />
  );
};

export default CycleTimeTrendWidget;
